import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { Surface, Btn, Field, Input, Select, Textarea } from "./Primitives";
import { Plus, Trash, Wrench, CheckCircle, Receipt } from "@phosphor-icons/react";

const STATUS_BADGE = {
  draft:     { label: "DRAFT",     cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" },
  open:      { label: "OPEN",      cls: "bg-amber/20 text-amber border-amber/60" },
  completed: { label: "COMPLETED", cls: "bg-cyan-500/20 text-cyan-200 border-cyan-500/60" },
  closed:    { label: "INVOICED",  cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/60" },
};

const TARGET_LABEL = {
  part: "Part",
  maintenance_item: "Maintenance item",
  ad_compliance: "AD compliance",
  custom: "Custom",
};

function StatusPill({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.draft;
  return (
    <span className={`tag font-mono text-[10px] tracking-wider border ${s.cls}`} style={{ padding: "2px 8px" }}>
      {s.label}
    </span>
  );
}

export default function WorkOrdersTab({ aircraftId, isOwner, aircraft }) {
  const [orders, setOrders] = useState([]);
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([]);
  const [adRows, setAdRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(null);
  const [closeForm, setCloseForm] = useState({ create_expense: true, expense_category: "maintenance" });

  const load = useCallback(async () => {
    const [w, p, m, a] = await Promise.all([
      api.get(`/work-orders?aircraft_id=${aircraftId}`).then((r) => r.data),
      api.get(`/aircraft/${aircraftId}/parts`).then((r) => r.data).catch(() => []),
      api.get(`/aircraft/${aircraftId}/maintenance-items`).then((r) => r.data).catch(() => []),
      api.get(`/aircraft/${aircraftId}/ad-compliance`).then((r) => r.data).catch(() => []),
    ]);
    setOrders(w); setParts(p); setItems(m); setAdRows(a);
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => orders.reduce(
    (acc, w) => ({
      draft: acc.draft + (w.status === "draft" ? 1 : 0),
      open: acc.open + (w.status === "open" ? 1 : 0),
      completed: acc.completed + (w.status === "completed" ? 1 : 0),
      closed: acc.closed + (w.status === "closed" ? 1 : 0),
      cost: acc.cost + (w.total_cost_eur || 0),
    }), { draft: 0, open: 0, completed: 0, closed: 0, cost: 0 },
  ), [orders]);

  const openCreate = () => setEditing({
    title: "", aircraft_id: aircraftId, status: "open",
    shop_name: "", items: [],
    labour_hours: null, labour_rate_eur: null, parts_cost_eur: null,
    other_cost_eur: null,
  });

  const addItem = (target) => {
    const next = [...(editing.items || []), { target, target_id: "", description: "", labour_hours: null, parts_cost_eur: null }];
    setEditing({ ...editing, items: next });
  };

  const updItem = (idx, patch) => {
    const next = [...editing.items];
    next[idx] = { ...next[idx], ...patch };
    setEditing({ ...editing, items: next });
  };

  const rmItem = (idx) => setEditing({ ...editing, items: editing.items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!editing) return;
    const payload = {
      aircraft_id: aircraftId,
      title: editing.title,
      status: editing.status || "open",
      shop_name: editing.shop_name || null,
      shop_invoice_ref: editing.shop_invoice_ref || null,
      opened_date: editing.opened_date || null,
      labour_hours: editing.labour_hours,
      labour_rate_eur: editing.labour_rate_eur,
      parts_cost_eur: editing.parts_cost_eur,
      other_cost_eur: editing.other_cost_eur,
      notes: editing.notes || null,
      items: (editing.items || []).map((it) => ({
        target: it.target,
        target_id: it.target_id || null,
        description: it.description || null,
        labour_hours: it.labour_hours,
        parts_cost_eur: it.parts_cost_eur,
        notes: it.notes || null,
      })).filter((it) => it.target),
    };
    // Strip nulls so backend accepts
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== ""));
    if (editing.id) {
      await api.patch(`/work-orders/${editing.id}`, clean);
    } else {
      await api.post("/work-orders", clean);
    }
    setEditing(null);
    load();
  };

  const transitionStatus = async (wo, status) => {
    await api.post(`/work-orders/${wo.id}/status`, { status });
    load();
  };

  const submitClose = async () => {
    if (!closing) return;
    await api.post(`/work-orders/${closing.id}/status`, { status: "closed", ...closeForm });
    setClosing(null);
    setCloseForm({ create_expense: true, expense_category: "maintenance" });
    load();
  };

  const del = async (wo) => {
    if (!confirm("Delete this work order?")) return;
    await api.delete(`/work-orders/${wo.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <Surface className="p-6" data-testid="wo-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2"><Wrench size={14} className="text-amber" />Maintenance work orders</div>
            <div className="text-xs text-zinc-500 mt-1">Bundle shop visits — link parts replacements, inspections, and AD compliance to one invoice.</div>
          </div>
          <div className="flex items-center gap-5">
            {(["open", "completed", "closed"]).map((s) => (
              <div key={s} className="text-right" data-testid={`wo-count-${s}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{STATUS_BADGE[s].label}</div>
                <div className="text-2xl font-mono">{totals[s]}</div>
              </div>
            ))}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Tracked spend</div>
              <div className="text-2xl font-mono text-amber">€{totals.cost.toFixed(0)}</div>
            </div>
          </div>
        </div>
        {isOwner && (
          <div className="mt-5">
            <Btn onClick={openCreate} data-testid="add-wo-btn"><Plus size={14} className="inline mr-1.5" />New work order</Btn>
          </div>
        )}
      </Surface>

      <Surface className="p-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
              <th className="text-left py-2.5 px-3">Title</th>
              <th className="text-left py-2.5 px-3">Shop</th>
              <th className="text-left py-2.5 px-3">Items</th>
              <th className="text-right py-2.5 px-3">Cost</th>
              <th className="text-left py-2.5 px-3">Status</th>
              <th className="text-right py-2.5 px-3"></th>
            </tr>
          </thead>
          <tbody data-testid="wo-table">
            {orders.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-zinc-500">No work orders yet.</td></tr>}
            {orders.map((wo) => (
              <tr key={wo.id} className="border-b border-zinc-900" data-testid={`wo-row-${wo.id}`}>
                <td className="py-3 px-3">
                  <div className="text-zinc-200">{wo.title}</div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5">opened {fmtDate(wo.opened_date)}{wo.completed_date ? ` · completed ${fmtDate(wo.completed_date)}` : ""}</div>
                </td>
                <td className="py-3 px-3 text-zinc-400 text-xs font-mono">
                  {wo.shop_name || "—"}
                  {wo.shop_invoice_ref && <div className="text-[10px] text-zinc-600">inv {wo.shop_invoice_ref}</div>}
                </td>
                <td className="py-3 px-3 text-xs font-mono text-zinc-400">{(wo.items || []).length} item{(wo.items || []).length === 1 ? "" : "s"}</td>
                <td className="py-3 px-3 text-right font-mono text-amber">{wo.total_cost_eur ? `€${wo.total_cost_eur.toFixed(0)}` : "—"}</td>
                <td className="py-3 px-3"><StatusPill status={wo.status} /></td>
                <td className="py-3 px-3 text-right">
                  {isOwner && (
                    <div className="flex justify-end gap-2 text-[10px] uppercase tracking-[0.2em] font-mono">
                      {wo.status === "draft" && <button onClick={() => transitionStatus(wo, "open")} className="text-amber hover:underline" data-testid={`wo-open-${wo.id}`}>Open</button>}
                      {wo.status === "open" && <button onClick={() => transitionStatus(wo, "completed")} className="text-cyan-300 hover:underline" data-testid={`wo-complete-${wo.id}`}><CheckCircle size={11} className="inline mr-1" />Complete</button>}
                      {wo.status === "completed" && <button onClick={() => { setClosing(wo); setCloseForm({ create_expense: true, expense_category: "maintenance" }); }} className="text-emerald-300 hover:underline" data-testid={`wo-close-${wo.id}`}><Receipt size={11} className="inline mr-1" />Invoice</button>}
                      {wo.status !== "closed" && <button onClick={() => setEditing(wo)} className="text-zinc-400 hover:text-amber" data-testid={`wo-edit-${wo.id}`}>Edit</button>}
                      {(wo.status === "draft" || wo.status === "open") && <button onClick={() => del(wo)} className="text-zinc-500 hover:text-red-400" data-testid={`wo-del-${wo.id}`}><Trash size={12} /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Surface>

      {editing && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="wo-modal">
          <Surface className="w-full max-w-4xl p-8 max-h-[92vh] overflow-y-auto">
            <h3 className="text-xl font-medium mb-5">{editing.id ? "Edit work order" : "New work order"}</h3>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <Field label="Title *"><Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Annual + 100h inspection" data-testid="wo-title-input" /></Field>
              <Field label="Shop"><Input value={editing.shop_name || ""} onChange={(e) => setEditing({ ...editing, shop_name: e.target.value })} data-testid="wo-shop-input" /></Field>
              <Field label="Opened"><Input type="date" value={editing.opened_date || ""} onChange={(e) => setEditing({ ...editing, opened_date: e.target.value })} /></Field>
              <Field label="Invoice ref"><Input value={editing.shop_invoice_ref || ""} onChange={(e) => setEditing({ ...editing, shop_invoice_ref: e.target.value })} /></Field>
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Linked items</div>
            {editing.items && editing.items.length > 0 && (
              <div className="space-y-2 mb-3" data-testid="wo-items-list">
                {editing.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start border border-zinc-800 p-3">
                    <div className="col-span-2"><Field label="Target">
                      <Select value={it.target} onChange={(e) => updItem(idx, { target: e.target.value, target_id: "" })} data-testid={`wo-item-target-${idx}`}>
                        {Object.entries(TARGET_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </Select>
                    </Field></div>
                    <div className="col-span-4"><Field label={it.target === "custom" ? "Description" : "Linked record"}>
                      {it.target === "custom" ? (
                        <Input value={it.description || ""} onChange={(e) => updItem(idx, { description: e.target.value })} placeholder="Inspect prop nicks" />
                      ) : (
                        <Select value={it.target_id || ""} onChange={(e) => updItem(idx, { target_id: e.target.value })} data-testid={`wo-item-link-${idx}`}>
                          <option value="">—</option>
                          {it.target === "part" && parts.map((p) => <option key={p.id} value={p.id}>{p.name} {p.part_number ? `(${p.part_number})` : ""}</option>)}
                          {it.target === "maintenance_item" && items.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.category}</option>)}
                          {it.target === "ad_compliance" && adRows.map((a) => <option key={a.id} value={a.id}>{a.ad?.reference} — {a.ad?.title?.substring(0, 40)}</option>)}
                        </Select>
                      )}
                    </Field></div>
                    <div className="col-span-2"><Field label="Labour h"><Input type="number" step="0.1" value={it.labour_hours ?? ""} onChange={(e) => updItem(idx, { labour_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`wo-item-labour-${idx}`} /></Field></div>
                    <div className="col-span-3"><Field label="Parts cost (€)"><Input type="number" step="0.01" value={it.parts_cost_eur ?? ""} onChange={(e) => updItem(idx, { parts_cost_eur: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`wo-item-parts-${idx}`} /></Field></div>
                    <button onClick={() => rmItem(idx)} className="col-span-1 h-9 text-zinc-500 hover:text-red-400 self-end mb-1.5" data-testid={`wo-item-remove-${idx}`}><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mb-5 flex-wrap">
              <Btn variant="ghost" onClick={() => addItem("part")} data-testid="wo-add-part-link"><Plus size={11} className="inline mr-1" />Add part</Btn>
              <Btn variant="ghost" onClick={() => addItem("maintenance_item")} data-testid="wo-add-maint-link"><Plus size={11} className="inline mr-1" />Add maintenance item</Btn>
              <Btn variant="ghost" onClick={() => addItem("ad_compliance")} data-testid="wo-add-ad-link"><Plus size={11} className="inline mr-1" />Add AD compliance</Btn>
              <Btn variant="ghost" onClick={() => addItem("custom")} data-testid="wo-add-custom-link"><Plus size={11} className="inline mr-1" />Custom task</Btn>
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Cost</div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Labour hours"><Input type="number" step="0.1" value={editing.labour_hours ?? ""} onChange={(e) => setEditing({ ...editing, labour_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="wo-cost-labour" /></Field>
              <Field label="Labour rate (€/h)"><Input type="number" step="0.01" value={editing.labour_rate_eur ?? ""} onChange={(e) => setEditing({ ...editing, labour_rate_eur: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="wo-cost-rate" /></Field>
              <Field label="Parts (€)"><Input type="number" step="0.01" value={editing.parts_cost_eur ?? ""} onChange={(e) => setEditing({ ...editing, parts_cost_eur: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="wo-cost-parts" /></Field>
              <Field label="Other (€)"><Input type="number" step="0.01" value={editing.other_cost_eur ?? ""} onChange={(e) => setEditing({ ...editing, other_cost_eur: e.target.value === "" ? null : parseFloat(e.target.value) })} /></Field>
            </div>

            <div className="mt-4"><Field label="Notes"><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field></div>

            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn onClick={save} disabled={!editing.title} data-testid="wo-save-btn">{editing.id ? "Save" : "Create work order"}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {closing && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="wo-close-modal">
          <Surface className="w-full max-w-md p-8">
            <h3 className="text-xl font-medium mb-2">Invoice work order</h3>
            <div className="text-sm text-zinc-300 mb-1">{closing.title}</div>
            <div className="text-sm text-zinc-500 mb-5">Total <span className="font-mono text-amber">€{(closing.total_cost_eur || 0).toFixed(2)}</span></div>
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={closeForm.create_expense} onChange={(e) => setCloseForm({ ...closeForm, create_expense: e.target.checked })} className="mt-1" data-testid="wo-close-create-expense" />
              <span className="text-sm text-zinc-300">Create a tracked expense for the cost-share engine</span>
            </label>
            {closeForm.create_expense && (
              <Field label="Expense category">
                <Select value={closeForm.expense_category} onChange={(e) => setCloseForm({ ...closeForm, expense_category: e.target.value })} data-testid="wo-close-category">
                  <option value="maintenance">maintenance</option>
                  <option value="fuel">fuel</option>
                  <option value="insurance">insurance</option>
                  <option value="hangar">hangar</option>
                  <option value="other">other</option>
                </Select>
              </Field>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setClosing(null)}>Cancel</Btn>
              <Btn onClick={submitClose} data-testid="wo-close-submit">Mark invoiced</Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
